import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// Import your reusable components
import ChatHeader from '../ChatHeader';
import Message from '../Message';
import MessageInput from '../MessageInput';
import ImageModal from '../ImageModal';

function ChatPage() {
    // This is the ONLY place 'brandFromUrl' is used. It is NOT used anywhere else.
    const { brandName: brandFromUrl } = useParams();
    const navigate = useNavigate();

    // This is the single source of truth for the brand.
    const [effectiveBrand, setEffectiveBrand] = useState(null);

    // All your state management and logic lives here
    const [messages, setMessages] = useState([]);
    const [userInput, setUserInput] = useState('');
    const [summary, setSummary] = useState({});
    const [zoomedImage, setZoomedImage] = useState(null);
    const chatWindowRef = useRef(null);
    const [languages, setLanguages] = useState([]);
    const [selectedLanguage, setSelectedLanguage] = useState(null);
    const [products, setProducts] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState(new Set());

    // This logic is driven by `effectiveBrand`.
    const brandConfig = useMemo(() => {
        if (!effectiveBrand) return null;
        const displayName = effectiveBrand.charAt(0).toUpperCase() + effectiveBrand.slice(1);
        return {
            name: effectiveBrand,
            displayName: displayName,
            initialMessage: `Bonjour, je suis l'assistant du support ${displayName}. Comment puis-je vous aider?`,
            supportTitle: `${displayName} support`,
            assistantName: `${displayName} assistant`,
            logo: `/${effectiveBrand}_logo.png`,
            bubbleIcon: `/${effectiveBrand}_bubble_180.png`
        };
    }, [effectiveBrand]);

    // Core logic to determine and validate the brand
    useEffect(() => {
        let determinedBrand = brandFromUrl;

        if (!determinedBrand) {
            const hostname = window.location.hostname;
            if (hostname === 'support-agent.myindygo.com') {
                determinedBrand = 'indygo';
            } else {
                // Use env variable for local development, fallback to 'indygo'
                determinedBrand = process.env.REACT_APP_BRAND_NAME || 'indygo';
            }
        }

        const validBrands = ['indygo', 'attentive', 'solem'];
        if (validBrands.includes(determinedBrand)) {
            setEffectiveBrand(determinedBrand);
        } else {
            navigate('/attentive', { replace: true });
        }
    }, [brandFromUrl, navigate]);

    // Set initial message
    useEffect(() => {
        if (brandConfig) {
            setMessages([{ text: brandConfig.initialMessage, sender: 'ai' }]);
        }
    }, [brandConfig]);

    const handleSendMessage = async (e) => {
        if (e) e.preventDefault();
        if (!userInput.trim()) return;

        const userQuery = userInput;
        const newUserMessage = { text: userQuery, sender: 'user' };

        setMessages(prevMessages => [...prevMessages, newUserMessage, { text: '', sender: 'ai' }]);
        setUserInput('');

        try {
            const response = await fetch('/api/chat', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    message: userQuery,
                    summary: summary,
                    language: selectedLanguage ? selectedLanguage.llm_value : undefined,
                    brand: effectiveBrand, // Uses the correct variable
                    // Only sent when the user restricted the grid to a subset —
                    // all selected (default) or none selected means "no filter".
                    selectedProducts:
                        selectedProducts.size > 0 && selectedProducts.size < products.length
                            ? Array.from(selectedProducts)
                            : undefined
                }),
            });

            if (!response.ok) throw new Error('Network response was not ok');
            if (!response.body) throw new Error('Response body is missing');
            const summaryHeader = response.headers.get('X-Conversation-Summary');
            if (summaryHeader) { try { const newSummary = JSON.parse(summaryHeader); console.log("Received new summary from header:", newSummary); setSummary(newSummary); } catch (err) { console.error("Failed to parse summary from header:", err); } }
            const rewrittenHeader = response.headers.get('X-Rewritten-Query');
            if (rewrittenHeader) {
                try {
                    const rewrittenQuery = JSON.parse(rewrittenHeader);
                    if (rewrittenQuery) {
                        const labels = { FRENCH: 'Question interprétée', ENGLISH: 'Interpreted as', SPANISH: 'Pregunta interpretada', GERMAN: 'Interpretierte Frage', ITALIAN: 'Domanda interpretata' };
                        const rewrittenLabel = labels[selectedLanguage?.llm_value] || labels.FRENCH;
                        setMessages(prev => {
                            const updatedMessages = [...prev];
                            const lastMessage = updatedMessages[updatedMessages.length - 1];
                            updatedMessages[updatedMessages.length - 1] = { ...lastMessage, rewrittenQuery, rewrittenLabel };
                            return updatedMessages;
                        });
                    }
                } catch (err) { console.error("Failed to parse rewritten query from header:", err); }
            }
            const reader = response.body.getReader(); const decoder = new TextDecoder(); let completeAiResponse = '';
            while (true) { const { done, value } = await reader.read(); if (done) break; const chunk = decoder.decode(value, { stream: true }); completeAiResponse += chunk; setMessages(prev => { const updatedMessages = [...prev]; const lastMessage = updatedMessages[updatedMessages.length - 1]; updatedMessages[updatedMessages.length - 1] = { ...lastMessage, text: lastMessage.text + chunk }; return updatedMessages; }); }
        } catch (error) {
            console.error('Error fetching AI response:', error);
            setMessages(prev => { const updatedMessages = [...prev]; const lastMessage = updatedMessages[updatedMessages.length - 1]; updatedMessages[updatedMessages.length - 1] = { ...lastMessage, text: "Sorry, I'm having trouble connecting." }; return updatedMessages; });
        }
    };

    const handleImageClick = (imageUrl) => setZoomedImage(imageUrl);
    const handleCloseModal = () => setZoomedImage(null);

    useEffect(() => {
        const fetchLanguages = async () => {
            if (!effectiveBrand) return; // Uses the correct variable
            try {
                const response = await fetch('/api/languages', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ brand: effectiveBrand }) // Uses the correct variable
                });
                if (!response.ok) throw new Error('Failed to fetch languages');
                const dataAsObject = await response.json();
                const dataAsArray = Object.keys(dataAsObject).map(key => ({ ui_flag: key, native_name: dataAsObject[key].native_name, llm_value: dataAsObject[key].llm_instruction_language }));
                if (!Array.isArray(dataAsArray) || dataAsArray.length === 0) { console.error("Error: Failed to transform languages object into an array.", dataAsObject); return; }
                setLanguages(dataAsArray);
                const defaultLang = dataAsArray.find(lang => lang.llm_value === 'FRENCH') || dataAsArray[0];
                setSelectedLanguage(defaultLang);
                console.log("Languages loaded and transformed, default set to:", defaultLang);
            } catch (error) {
                console.error("Error fetching languages:", error);
            }
        };
        fetchLanguages();
    }, [effectiveBrand]); // Uses the correct dependency

    useEffect(() => {
        const fetchProducts = async () => {
            if (!effectiveBrand) return;
            try {
                const response = await fetch('/api/products', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ brand: effectiveBrand })
                });
                if (!response.ok) throw new Error('Failed to fetch products');
                const productList = await response.json();
                if (!Array.isArray(productList)) { console.error("Error: /api/products did not return an array.", productList); return; }
                setProducts(productList);
                setSelectedProducts(new Set(productList)); // all selected by default
                console.log(`Products loaded: ${productList.length}`);
            } catch (error) {
                console.error("Error fetching products:", error);
            }
        };
        fetchProducts();
    }, [effectiveBrand]);

    useEffect(() => {
        const chatWindow = chatWindowRef.current;
        if (!chatWindow) return;
        const distanceFromBottom = chatWindow.scrollHeight - chatWindow.scrollTop - chatWindow.clientHeight;
        if (distanceFromBottom < 50) {
            chatWindow.scrollTop = chatWindow.scrollHeight;
        }
    }, [messages]);

    if (!brandConfig) {
        return null;
    }

    return (
        <div className="flex flex-col h-[100dvh] bg-gray-100">
            <ChatHeader
                languages={languages}
                selectedLanguage={selectedLanguage}
                onLanguageChange={setSelectedLanguage}
                brandConfig={brandConfig}
                products={products}
                selectedProducts={selectedProducts}
                onProductSelectionChange={setSelectedProducts}
            />
            <div className="flex-grow w-full flex flex-col overflow-hidden">
                <div className="flex-grow w-full bg-white overflow-hidden md:max-w-3xl md:mx-auto md:shadow-lg flex flex-col">
                    <div ref={chatWindowRef} className="flex-1 overflow-y-auto bg-gray-50">
                        {messages.map((msg, index) => (
                            <Message key={index} {...msg} onImageClick={handleImageClick} brandConfig={brandConfig} />
                        ))}
                    </div>
                    <MessageInput
                        userInput={userInput}
                        setUserInput={setUserInput}
                        onSendMessage={handleSendMessage}
                    />
                </div>
            </div>
            {zoomedImage && <ImageModal imageUrl={zoomedImage} onClose={handleCloseModal} />}
        </div>
    );
}

export default ChatPage;